import * as z from "zod";
import { isValidPhone } from "../../../utils/validators";

const emptyIfNullish = (v: unknown) => v ?? "";

const req = (msg: string) =>
  z.preprocess(emptyIfNullish, z.string().trim().min(1, msg));

const numberPreprocess = z.preprocess((val) => {
  if (val === null || val === undefined || val === "") return 0;

  const num = Number(val);
  return Number.isNaN(num) ? 0 : num;
}, z.number());

const optionalNumberPreprocess = z.preprocess((val) => {
  if (val === null || val === undefined || val === "") return undefined;

  const num = Number(val);
  return Number.isNaN(num) ? undefined : num;
}, z.number().optional());

export const schema = z
  .object({
    // Company Information
    company_name: req("Please enter company name."),
    website: req("Please enter domain."),
    tax_id: z.string().optional(),

    // Company contact + address
    company_email: z.preprocess(
      emptyIfNullish,
      z.string().trim().min(1, "Please enter company email ID.")
    ),
    company_phone: z.preprocess(emptyIfNullish, z.string().trim().optional()),
    company_work_phone_no: z.string().optional(),
    company_street_address: z.string().optional(),
    company_country_id: numberPreprocess.refine(
      (val) => val > 0,
      "Please select company country."
    ),
    company_state_id: numberPreprocess.refine(
      (val) => val > 0,
      "Please select company state."
    ),
    company_city_id: numberPreprocess.refine(
      (val) => val > 0,
      "Please select company city."
    ),
    company_postal_code: z.preprocess(
      emptyIfNullish,
      z.string().trim().optional()
    ),

    region_id: numberPreprocess.refine(
      (val) => val > 0,
      "Please select region."
    ),
    timezone: req("Please select timezone."),
    msp_id: numberPreprocess.refine((val) => val > 0, "MSP ID is required."),

    // Primary Contact
    first_name: req("Please enter first name."),
    last_name: req("Please enter last name."),
    corporate_email: z.preprocess(
      emptyIfNullish,
      z.string().trim().min(1, "Please enter email.")
    ),
    mobile_phone: z.preprocess(
      emptyIfNullish,
      z
        .string()
        .trim()
        .min(1, "Please enter mobile number.")
        .refine(isValidPhone, { message: "Enter a valid mobile number." })
    ),
    work_phone_no: z.string().optional(),
    designation: req("Please enter designation."),
    position: req("Please enter position."),

    // Primary Contact Address
    street_address: z.string().optional(),
    country_id: numberPreprocess.refine(
      (val) => val > 0,
      "Please select country."
    ),
    state_id: numberPreprocess.refine((val) => val > 0, "Please select state."),
    city_id: numberPreprocess.refine((val) => val > 0, "Please select city."),
    postal_code: z.preprocess(emptyIfNullish, z.string().trim().optional()),

    // Product (API Integrated)
    product: req("Please choose a product."),
    billing_frequency: req("Please choose billing frequency."),
    no_of_licenses: numberPreprocess.refine(
      (val) => val >= 1,
      "Number of licenses must be at least 1."
    ),

    total_paid_amount: numberPreprocess.refine(
      (val) => val >= 0,
      "Total amount must be a positive number."
    ),

    discount: optionalNumberPreprocess.refine(
      (val) => val === undefined || (val >= 0 && val <= 100),
      { message: "Discount must be between 0 and 100." }
    ),
    payment_mode: req("Please choose payment mode."),
    transaction_ref: z.string().optional(),
    date_of_payment: req("Please select date of payment."),
    expiration_date: req("Please select subscription expiration date."),
    comment: z.string().optional().default(""),
  })
  .superRefine((data, ctx) => {
    if (
      data.date_of_payment &&
      data.expiration_date &&
      data.expiration_date < data.date_of_payment
    ) {
      ctx.addIssue({
        code: "custom",
        path: ["expiration_date"],
        message: "Subscription expiration date must be after payment date.",
      });
    }
  });

export type FormValues = z.infer<typeof schema>;
